/**
 * Design principle: Information density + actionability in chat format
 */

import type { FusedRecommendation } from '@/services/recommendations-fusion.service';

interface CollectionFormatOptions {
  includeBookingLinks?: boolean;
  includeMaps?: boolean;
  includeDescription?: boolean; // controls one-liner inclusion
  maxVenues?: number;
  curatorName?: string;
  curatorContext?: string;
  friendsInNetwork?: string[];
  collectionUrl?: string;
}

interface FormattedVenue {
  name: string;
  category: string;
  neighborhood?: string;
  oneLiner?: string;
  bookingInfo?: string;
  mapLink?: string;
  socialProof?: string;
}

function formatVenue(venue: FormattedVenue, index: number): string {
  const parts: string[] = [];
  
  parts.push(`${index}. *${venue.name}*`);
  
  const contextParts = [];
  if (venue.category) {
    const emoji = getCategoryEmoji(venue.category);
    contextParts.push(`${emoji} ${venue.category}`);
  }
  if (venue.neighborhood) {
    contextParts.push(`📍 ${venue.neighborhood}`);
  }
  if (contextParts.length > 0) {
    parts.push(`   ${contextParts.join(' • ')}`);
  }
  
  if (venue.oneLiner) {
    parts.push(`   _${venue.oneLiner}_`);
  }
  
  if (venue.socialProof) {
    parts.push(`   👥 ${venue.socialProof}`);
  }
  
  const actions = [];
  if (venue.bookingInfo) {
    actions.push(`📞 ${venue.bookingInfo}`);
  }
  if (venue.mapLink) {
    actions.push(`🗺 ${venue.mapLink}`);
  }
  if (actions.length > 0) {
    parts.push(`   ${actions.join(' | ')}`);
  }
  
  return parts.join('\n');
}

function getCategoryEmoji(category: string): string {
  const emojiMap: Record<string, string> = {
    'restaurants': '🍽',
    'bars': '🍸',
    'cafes': '☕',
    'clubs': '🎵',
    'activities': '🎯',
    'culture': '🎨',
    'shopping': '🛍',
    'hotels': '🏨',
    'wellness': '🧘'
  };
  return emojiMap[category.toLowerCase()] || '📍';
}

function formatHeader(
  city: string,
  curatorName?: string,
  curatorContext?: string,
  friendsInNetwork?: string[]
): string {
  const parts: string[] = [];
  
  if (curatorName) {
    parts.push(`📍 *${curatorName}'s ${city} Picks*`);
  } else {
    parts.push(`📍 *${city} Collection*`);
  }
  
  if (curatorContext) {
    parts.push(`_${curatorContext}_`);
  }
  
  if (friendsInNetwork && friendsInNetwork.length > 0) {
    const friendsText = friendsInNetwork.length <= 3 
      ? friendsInNetwork.join(', ')
      : `${friendsInNetwork.slice(0, 2).join(', ')} + ${friendsInNetwork.length - 2} others`;
    parts.push(`✓ Trusted by ${friendsText}`);
  }
  
  parts.push(''); // Empty line for spacing
  return parts.join('\n');
}

function formatFooter(
  totalVenues: number,
  shownVenues: number,
  collectionUrl?: string
): string {
  const parts: string[] = [];
  
  parts.push(''); // Empty line for spacing
  
  if (totalVenues > shownVenues) {
    parts.push(`_📱 ${totalVenues - shownVenues} more spots in full collection_`);
  }
  
  if (collectionUrl) {
    parts.push(`🔗 *See all + get booking help:*`);
    parts.push(collectionUrl);
  }
  
  return parts.join('\n');
}

export function formatCollectionForWhatsApp(
  venues: FusedRecommendation[],
  city: string,
  options: CollectionFormatOptions = {}
): string {
  const {
    maxVenues = 5,
    curatorName,
    curatorContext,
    friendsInNetwork,
    collectionUrl,
    includeBookingLinks = true,
    includeMaps = false,
    includeDescription = false
  } = options;
  
  // Limit venues for message length
  const venuesToShow = venues.slice(0, maxVenues);
  
  const formattedVenues = venuesToShow.map((venue, index) => {
    const formatted: FormattedVenue = {
      name: venue.place_name,
      category: venue.category,
      neighborhood: venue.detected_neighborhood || venue.neighborhood
    };
    
    if (includeDescription && index < 3 && venue.description) {
      formatted.oneLiner = venue.description.split('.')[0].trim();
      if (formatted.oneLiner.length > 60) {
        formatted.oneLiner = formatted.oneLiner.substring(0, 57) + '...';
      }
    }
    
    if (venue.friend_count && venue.friend_count > 0) {
      formatted.socialProof = `${venue.friend_count} friend${venue.friend_count > 1 ? 's' : ''} saved`;
    } else if (venue.curator_count && venue.curator_count > 0) {
      formatted.socialProof = `Curator pick`;
    }
    
    if (includeBookingLinks) {
      if (venue.phone) {
        formatted.bookingInfo = venue.phone;
      } else if (venue.booking_url) {
        formatted.bookingInfo = 'Book';
      }
    }
    
    // Add map link (shortened for mobile)
    if (includeMaps && venue.google_place_id) {
      formatted.mapLink = 'Map';
    }
    
    return formatVenue(formatted, index + 1);
  });
  
  const header = formatHeader(city, curatorName, curatorContext, friendsInNetwork);
  const body = formattedVenues.join('\n\n');
  const footer = formatFooter(venues.length, venuesToShow.length, collectionUrl);
  
  return `${header}${body}${footer}`;
}

export function createShareableLink(shareToken: string, source: 'whatsapp' | 'instagram' | 'direct' = 'whatsapp'): string {
  // In production, use a URL shortener service
  const baseUrl = window.location.origin;
  return `${baseUrl}/l/${shareToken}?src=${source}`;
}

export function generateWhatsAppUrl(message: string, phoneNumber?: string): string {
  const encodedMessage = encodeURIComponent(message);
  
  if (phoneNumber) {
    // Direct to specific number (for concierge features)
    return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
  }
  
  return `https://wa.me/?text=${encodedMessage}`;
}

/**
 * Copy formatted text to clipboard (mobile-friendly)
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for mobile browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand('copy');
      textArea.remove();
      return success;
    }
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
}

export function formatForInstagram(
  venues: FusedRecommendation[],
  city: string,
  curatorName?: string
): string {
  const title = curatorName 
    ? `${curatorName}'s ${city} Essentials`
    : `${city} Hidden Gems`;
    
  const venueList = venues.slice(0, 5).map((v, i) => 
    `${i + 1}. ${v.place_name} ${getCategoryEmoji(v.category)}`
  ).join('\n');
  
  return `${title}\n\n${venueList}\n\nSave this for your next trip 📌`;
}
